import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import back4app2 from '../back4app2';
import b4aLogo from '../assets/images/b4a-sqaure-logo.svg';
import {
  BACK4APP_DOT_COM_DASHBOARD_URL,
  BACK4APP_DOT_COM_SITE_URL,
  TURNSTILE_SITE_KEY,
} from '../settings';
import { Card, Fine, MONO, Page, Result, SORA, Text, Title } from '../components/AgentLoginLayout';
import Turnstile from '../components/Turnstile';
import {
  ApiV2Error,
  approveLoginRequest,
  approveLoginWithoutAccount,
  denyLoginRequest,
  getDeviceLoginRequest,
  getLoginRequest,
  isSignedOut,
  LoginRequest,
  SignedOutLoginRequest,
} from '../utils/apiV2';

/**
 * The authorization screen of the agents' login: where GET /oauth/authorize
 * (API v2) sends the browser, with `?request=<id>`.
 *
 * An AI agent (Claude, Cursor) asked to act for a Back4App account. The
 * person sees who is asking and where the browser goes back to, and the click
 * on Authorize is the only thing that connects it. An account whose e-mail is
 * not confirmed yet waits here for the confirmation, and the login finishes
 * on its own.
 *
 * Nobody signed in: the screen offers to sign in or to continue without an
 * account. The second shows a captcha (Cloudflare Turnstile), and the API
 * makes a temporary account (24 hours, one app) and approves the login for
 * it; the agent gives the person the claim link to keep the app.
 *
 * The CLI's login ends here too, at /connect/device: the terminal shows a
 * short code and this address, the person opens it on any device, and the
 * code (in the address, or typed in) finds the login. Nothing sends the
 * browser back: the terminal sees the answer by itself. `b4p deploy
 * --temporary` asks for a login only a temporary account answers: the
 * screen offers "Continue without an account" alone, to anyone.
 *
 * Outside the dashboard's shell on purpose: it is a page of the login, not of
 * an app, and it must work for an account that has no access to Containers.
 */

type Step =
  | { name: 'loading' }
  | { name: 'enterCode'; wrong?: boolean }
  | { name: 'choose'; request: SignedOutLoginRequest; withoutAccount: boolean }
  | { name: 'connectedWithoutAccount'; device: boolean }
  | { name: 'authorize'; request: LoginRequest }
  | { name: 'waitingEmail'; request: LoginRequest }
  | { name: 'connected'; request: LoginRequest }
  | { name: 'cancelled'; request: LoginRequest }
  | { name: 'expired'; clientName?: string }
  | { name: 'unavailable'; title: string; text: string };

// How often the page asks whether the e-mail was confirmed.
const EMAIL_POLL_MS = 3000;

// How long "Continue without an account" waits for the captcha's answer
// before it says the check did not pass: Cloudflare may never answer.
const CAPTCHA_WAIT_MS = 20000;

const captchaFailedMessage = (signInOffered: boolean) =>
  signInOffered
    ? 'The check did not pass. Try again, or sign in. An ad blocker can stop it.'
    : 'The check did not pass. Try again. An ad blocker can stop it.';


// The site's login brings the person back to this page, to the same request.
const goToLogin = () => {
  window.location.replace(
    `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
  );
};

const isOwnComputer = (returnTo: string) =>
  /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/.test(returnTo);

const unavailable = (e: unknown, from = 'your AI agent'): Step => {
  if (e instanceof ApiV2Error && e.status === 410) {
    return { name: 'expired' };
  }
  if (e instanceof ApiV2Error && e.status === 409) {
    return {
      name: 'unavailable',
      title: 'This request was already answered',
      text: `Go back to ${from}. To connect again, start from there.`,
    };
  }
  if (e instanceof ApiV2Error && e.status === 404) {
    return {
      name: 'unavailable',
      title: 'This link is not valid',
      text: `Start the connection again from ${from}.`,
    };
  }
  return {
    name: 'unavailable',
    title: 'Something went wrong',
    text: `Reload this page. If it keeps happening, start again from ${from}.`,
  };
};

const ConnectAgent = ({ device = false }: { device?: boolean }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestId = searchParams.get('request') || '';
  // The CLI's login: the short code the terminal shows.
  const userCode = searchParams.get('user_code') || '';
  const from = device ? 'your terminal' : 'your AI agent';
  // Set by /oauth/authorize when the client or its return address is not a
  // registered one: nothing is sent back to that client.
  const linkError = searchParams.get('error');

  const [step, setStep] = useState<Step>({ name: 'loading' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resent, setResent] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [switching, setSwitching] = useState<'idle' | 'working' | 'failed'>('idle');
  const [typedCode, setTypedCode] = useState('');
  // "Continue without an account": the captcha's answer, and what the last
  // try said when it did not go through.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [captchaBroken, setCaptchaBroken] = useState(false);
  // The last check failed: the next click resets the widget to try again.
  const [captchaFailedOnce, setCaptchaFailedOnce] = useState(false);
  // The click on "Continue without an account" waits for the captcha's answer
  // (usually given with nothing to do) and then goes on by itself.
  const [continuing, setContinuing] = useState(false);
  const [withoutAccountError, setWithoutAccountError] = useState<string | null>(null);

  const load = useCallback(
    () => (device ? getDeviceLoginRequest(userCode) : getLoginRequest(requestId)),
    [device, userCode, requestId]
  );

  const finish = useCallback((request: LoginRequest, redirectTo: string | null) => {
    setStep({ name: 'connected', request });
    if (redirectTo) {
      window.location.assign(redirectTo);
    }
  }, []);

  useEffect(() => {
    if (device && !userCode) {
      setStep({ name: 'enterCode' });
      return;
    }
    if (!device && (linkError || !requestId)) {
      setStep(unavailable(new ApiV2Error(404, 'LOGIN_REQUEST_NOT_FOUND', '')));
      return;
    }

    let cancelled = false;
    setStep({ name: 'loading' });
    load()
      .then((request) => {
        if (cancelled) {
          return;
        }
        // Nobody signed in: sign in, or continue without an account. A
        // terminal's login is a person's (sign in), unless it asked for a
        // temporary account: then only that, whoever is signed in.
        if (isSignedOut(request)) {
          if (device && !request.temporaryOnly) {
            goToLogin();
          } else {
            setStep({ name: 'choose', request, withoutAccount: false });
          }
          return;
        }
        setStep(
          request.status === 'WAITING_EMAIL'
            ? { name: 'waitingEmail', request }
            : { name: 'authorize', request }
        );
      })
      .catch((e) => {
        if (cancelled) {
          return;
        }
        // Not signed in, with an API that does not answer signed-out screens.
        if (e instanceof ApiV2Error && e.status === 401) {
          goToLogin();
          return;
        }
        // A code that is of no login: typed wrong, or its time passed.
        if (device && e instanceof ApiV2Error && e.status === 404) {
          setStep({ name: 'enterCode', wrong: true });
          return;
        }
        setStep(unavailable(e, from));
      });

    return () => {
      cancelled = true;
    };
  }, [device, userCode, requestId, linkError, load, from]);

  // While the e-mail is not confirmed, Authorize is asked again: the first
  // answer after the confirmation carries the way back to the client.
  useEffect(() => {
    if (step.name !== 'waitingEmail') {
      return;
    }

    const { request } = step;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const approval = await approveLoginRequest(request.id);
        if (!cancelled && approval.status === 'APPROVED') {
          finish(request, approval.redirectTo);
        }
      } catch (e) {
        // A request that ended (the hour passed) stops the wait; a network
        // blip does not.
        if (!cancelled && e instanceof ApiV2Error && e.status >= 400 && e.status < 500) {
          setStep(
            e.status === 410 ? { name: 'expired', clientName: request.clientName } : unavailable(e, from)
          );
        }
      }
    }, EMAIL_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [step, finish, from]);

  const authorize = async (request: LoginRequest) => {
    setIsSubmitting(true);
    try {
      const approval = await approveLoginRequest(request.id);
      if (approval.status === 'APPROVED') {
        finish(request, approval.redirectTo);
      } else {
        setStep({ name: 'waitingEmail', request });
      }
    } catch (e) {
      setStep(
        e instanceof ApiV2Error && e.status === 410
          ? { name: 'expired', clientName: request.clientName }
          : unavailable(e, from)
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // `signInOffered`: false on a login only a temporary account answers,
  // where nothing here signs in, so nothing says to.
  const continueWithoutAccount = useCallback(async (id: string, token: string, signInOffered: boolean) => {
    setContinuing(false);
    setIsSubmitting(true);
    setWithoutAccountError(null);
    try {
      const approval = await approveLoginWithoutAccount(id, token);
      setStep({ name: 'connectedWithoutAccount', device });
      if (approval.status === 'APPROVED' && approval.redirectTo) {
        window.location.assign(approval.redirectTo);
      }
    } catch (e) {
      if (e instanceof ApiV2Error && [404, 409, 410].includes(e.status)) {
        setStep(unavailable(e, from));
        return;
      }
      // The captcha's answer was spent on this try: a new one is asked for.
      setCaptchaReset((n) => n + 1);
      setWithoutAccountError(
        e instanceof ApiV2Error && e.code === 'CAPTCHA_FAILED'
          ? 'The captcha was not accepted. Try it again.'
          : e instanceof ApiV2Error && e.status > 0 && e.status !== 500
          ? e.message
          : signInOffered
          ? 'Something went wrong. Try again in a moment, or sign in.'
          : 'Something went wrong. Try again in a moment.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [device, from]);

  // The captcha answered after the click: go on.
  useEffect(() => {
    if (continuing && captchaToken && step.name === 'choose') {
      continueWithoutAccount(step.request.id, captchaToken, !step.request.temporaryOnly);
    }
  }, [continuing, captchaToken, step, continueWithoutAccount]);

  // The captcha failed, or never answered: the click stops waiting and says
  // so, and the next click asks Cloudflare again.
  const captchaFailed = useCallback(() => {
    setCaptchaFailedOnce(true);
    if (continuing && step.name === 'choose') {
      setContinuing(false);
      setWithoutAccountError(captchaFailedMessage(!step.request.temporaryOnly));
    }
  }, [continuing, step]);

  useEffect(() => {
    if (!continuing || captchaToken) {
      return;
    }
    const timer = window.setTimeout(captchaFailed, CAPTCHA_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [continuing, captchaToken, captchaFailed]);

  const cancel = async (request: LoginRequest) => {
    setIsSubmitting(true);
    try {
      const { redirectTo } = await denyLoginRequest(request.id);
      setStep({ name: 'cancelled', request });
      // The client is told, so it stops waiting.
      if (redirectTo) {
        window.location.assign(redirectTo);
      }
    } catch (e) {
      setStep(unavailable(e, from));
    } finally {
      setIsSubmitting(false);
    }
  };

  // For whoever is signed in to the wrong account. The site's own sign-out
  // ends on its home page and the request would be lost on the way; this
  // route only ends the session, and the login then comes back here.
  const switchAccount = async () => {
    setSwitching('working');
    try {
      await fetch(`${BACK4APP_DOT_COM_DASHBOARD_URL}/logout/hub`, {
        method: 'POST',
        credentials: 'include',
        // Another origin, which sends no CORS headers: the answer cannot be
        // read, so the request is asked for again to see whether it worked.
        mode: 'no-cors',
      });
      await load();
      setSwitching('failed');
    } catch (e) {
      if (e instanceof ApiV2Error && e.status === 401) {
        goToLogin();
        return;
      }
      if (e instanceof ApiV2Error) {
        setStep(unavailable(e, from));
        return;
      }
      setSwitching('failed');
    }
  };

  const resend = async () => {
    setResent('sending');
    try {
      await back4app2.resendVerificationMail();
      setResent('sent');
    } catch (e) {
      setResent('failed');
    }
  };

  if (step.name === 'loading') {
    return <Page>{null}</Page>;
  }

  if (step.name === 'unavailable') {
    return <Result title={step.title} text={step.text} />;
  }

  if (step.name === 'enterCode') {
    const submitCode = (event: FormEvent) => {
      event.preventDefault();
      if (typedCode.trim()) {
        setSearchParams({ user_code: typedCode.trim().toUpperCase() });
      }
    };
    return (
      <Page>
        <Card>
          <div className="flex flex-col items-center gap-2.5">
            <Title>Enter the code</Title>
            <Text>It is in your terminal.</Text>
          </div>
          <form className="flex flex-col gap-3" onSubmit={submitCode}>
            <input
              type="text"
              aria-label="Code"
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={12}
              placeholder="XXXX-XXXX"
              className="h-[52px] rounded-lg border border-[#2D3C58] bg-transparent text-center text-xl uppercase tracking-[0.12em] text-white outline-none placeholder:text-[#4A5873] focus:border-[#5DBB72]"
              style={{ fontFamily: MONO }}
              value={typedCode}
              onChange={(event) => setTypedCode(event.target.value)}
            />
            <button
              type="submit"
              className="flex h-[52px] items-center justify-center rounded-lg bg-[#3F9A59] text-base font-semibold text-white hover:bg-[#368A4E] disabled:opacity-60"
              style={{ fontFamily: SORA }}
              disabled={!typedCode.trim()}
            >
              Continue
            </button>
          </form>
          {step.wrong && (
            <Fine>
              <span className="text-[#AEB8C8]">This code is not valid, or it expired.</span>
            </Fine>
          )}
        </Card>
      </Page>
    );
  }

  if (step.name === 'choose') {
    const { request: signedOut, withoutAccount } = step;
    const waiting = continuing || isSubmitting;
    // A build with no captcha key cannot make a temporary account: the option
    // is left out rather than offered and refused every time.
    const offersTemporary = !!TURNSTILE_SITE_KEY;
    // `deploy --temporary`: the account it asked for, and no sign-in.
    const onlyWithout = !!signedOut.temporaryOnly;
    if (onlyWithout && !offersTemporary) {
      return (
        <Result
          title="Temporary accounts are not available"
          text={`Create a free Back4App account, then deploy from ${from}.`}
        />
      );
    }
    return (
      <Page>
        <Card>
          <div className="flex flex-col items-center gap-4">
            <img src={b4aLogo} alt="Back4App" className="h-11 w-11 rounded-[10px]" />
            <Title large>Connect {signedOut.clientName || 'your AI agent'}</Title>
          </div>
          <div className="flex flex-col gap-3">
            {!onlyWithout && (
              <button
                type="button"
                className="flex h-[52px] items-center justify-center rounded-lg bg-[#3F9A59] text-base font-semibold text-white hover:bg-[#368A4E] disabled:opacity-60"
                style={{ fontFamily: SORA }}
                disabled={waiting}
                onClick={goToLogin}
              >
                Sign in
              </button>
            )}
            {offersTemporary && (
              <button
                type="button"
                className={
                  onlyWithout
                    ? 'flex h-[52px] items-center justify-center rounded-lg bg-[#3F9A59] text-base font-semibold text-white hover:bg-[#368A4E] disabled:opacity-60'
                    : 'flex h-[52px] items-center justify-center rounded-lg border border-[#2D3C58] text-base font-semibold text-white hover:border-[#5DBB72] disabled:opacity-60'
                }
                style={{ fontFamily: SORA }}
                disabled={waiting || captchaBroken}
                onClick={() => {
                  setWithoutAccountError(null);
                  if (captchaFailedOnce && !captchaToken) {
                    setCaptchaFailedOnce(false);
                    setCaptchaReset((n) => n + 1);
                  }
                  setContinuing(true);
                  if (!withoutAccount) {
                    setStep({ name: 'choose', request: signedOut, withoutAccount: true });
                  }
                }}
              >
                {waiting ? 'Connecting…' : 'Continue without an account'}
              </button>
            )}
            {/* Loaded on the first click: nobody who signs in loads it. */}
            {offersTemporary && withoutAccount && !captchaBroken && (
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                action="temporary-account"
                resetKey={captchaReset}
                onToken={setCaptchaToken}
                onFail={captchaFailed}
                onError={() => {
                  setCaptchaBroken(true);
                  setContinuing(false);
                }}
              />
            )}
          </div>
          <Fine>
            {captchaBroken ? (
              <span className="text-[#AEB8C8]">
                {onlyWithout
                  ? 'The check could not load. Reload this page.'
                  : 'The check could not load. Reload this page, or sign in.'}
              </span>
            ) : withoutAccountError ? (
              <span className="text-[#AEB8C8]">{withoutAccountError}</span>
            ) : offersTemporary ? (
              <span>Without an account: 24 hours and one app.</span>
            ) : null}
            {/* The terminal's code: the person checks it is their own. */}
            {signedOut.userCode && (
              <span>
                Code{' '}
                <span className="text-[#AEB8C8]" style={{ fontFamily: MONO }}>
                  {signedOut.userCode}
                </span>
                , as in your terminal
              </span>
            )}
            {/* Who asked, and where the code goes: not clicked blind. */}
            {signedOut.clientOrigin && (
              <span>
                Published at{' '}
                <span className="text-[#AEB8C8]" style={{ fontFamily: MONO }}>
                  {signedOut.clientOrigin}
                </span>
              </span>
            )}
            {signedOut.returnTo && (
              <span>
                Returns to{' '}
                <span className="text-[#AEB8C8]" style={{ fontFamily: MONO }}>
                  {signedOut.returnTo}
                </span>
              </span>
            )}
          </Fine>
        </Card>
      </Page>
    );
  }

  if (step.name === 'connectedWithoutAccount') {
    return step.device ? (
      <Result
        title="Your terminal is connected"
        text="Go back to your terminal: the deploy goes on there. You can close this tab."
        fine={<span>On a temporary account, for 24 hours.</span>}
      />
    ) : (
      <Result
        title="Your agent is connected"
        text="Taking you back to your AI agent. You can close this tab."
        fine={<span>On a temporary account, for 24 hours.</span>}
      />
    );
  }

  if (step.name === 'expired') {
    return (
      <Result
        title="This request expired"
        text={`Start again from ${device ? from : step.clientName || from}.`}
        fine={<span>Requests last {device ? 15 : 10} minutes.</span>}
      />
    );
  }

  const { request } = step;
  const client = request.clientName;

  if (step.name === 'cancelled') {
    return (
      <Result
        title="Connection cancelled"
        text={`${client} has no access to your account. You can close this tab.`}
      />
    );
  }

  if (step.name === 'connected') {
    return (
      <Result
        title={`${client} is connected`}
        text={
          device
            ? 'Go back to your terminal. You can close this tab.'
            : `Taking you back to ${client}. You can close this tab.`
        }
        fine={<span>Signed in as {request.signedInAs}</span>}
      />
    );
  }

  if (step.name === 'waitingEmail') {
    return (
      <Page>
        <Card>
          <div className="flex flex-col items-center gap-2.5">
            <Title>Confirm your email</Title>
            <Text>Open the link we sent to {request.signedInAs}. This page continues on its own.</Text>
          </div>
          <div className="flex items-center justify-center gap-2.5">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[#5DBB72]" />
            <span className="text-sm">Waiting for confirmation…</span>
          </div>
          <Fine>
            {resent === 'sent' ? (
              <span>Email sent again.</span>
            ) : (
              <button
                type="button"
                className="text-[#5DBB72] hover:text-[#7ED08F] hover:underline disabled:opacity-60"
                disabled={resent === 'sending'}
                onClick={resend}
              >
                Resend email
              </button>
            )}
            {resent === 'failed' && <span>The email could not be sent. Try again in a moment.</span>}
          </Fine>
        </Card>
        <p className="m-0 w-full max-w-[440px] text-center text-[13px] leading-[1.5] text-[#7F8BA0]">
          {client} is waiting too. Keep this tab open.
        </p>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-[#111B2E] text-[#E8ECF3]">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 17l6-5-6-5" />
                <path d="M12 19h8" />
              </svg>
            </div>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#7F8BA0"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 8h14l-3-3" />
              <path d="M20 16H6l3 3" />
            </svg>
            <img src={b4aLogo} alt="Back4App" className="h-11 w-11 rounded-[10px]" />
          </div>
          <Title large>Connect {client}</Title>
          <Text>
            It will deploy and manage your Back4App apps.
            <br />
            It can&apos;t see your password or payment details.
          </Text>
        </div>
        {request.userCode && (
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[13px] leading-[1.5] text-[#7F8BA0]">Your terminal shows this code</span>
            <span className="text-2xl tracking-[0.12em] text-white" style={{ fontFamily: MONO }}>
              {request.userCode}
            </span>
          </div>
        )}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            className="flex h-[52px] items-center justify-center rounded-lg bg-[#3F9A59] text-base font-semibold text-white hover:bg-[#368A4E] disabled:opacity-60"
            style={{ fontFamily: SORA }}
            disabled={isSubmitting}
            onClick={() => authorize(request)}
          >
            Authorize
          </button>
          <button
            type="button"
            className="self-center text-sm text-[#AEB8C8] hover:text-white hover:underline disabled:opacity-60"
            disabled={isSubmitting}
            onClick={() => cancel(request)}
          >
            Cancel
          </button>
        </div>
        <Fine>
          {!request.emailVerified && (
            <span className="text-[#AEB8C8]">
              Next, confirm your email: we sent a link to {request.signedInAs}.
            </span>
          )}
          {/* Two pieces that wrap whole: a long address pushes the link to
              its own line, with nothing left hanging. */}
          <span className="flex flex-wrap items-center justify-center gap-x-2">
            <span>Signed in as {request.signedInAs}</span>
            <button
              type="button"
              className="whitespace-nowrap text-[#5DBB72] hover:text-[#7ED08F] hover:underline disabled:opacity-60"
              disabled={isSubmitting || switching === 'working'}
              onClick={switchAccount}
            >
              Use another account
            </button>
          </span>
          {switching === 'failed' && (
            <span className="text-[#AEB8C8]">
              We couldn&apos;t sign you out. Sign out at back4app.com, then reload this page.
            </span>
          )}
          {device ? (
            <span className="text-[#F3C969]">Only continue if you started this login yourself.</span>
          ) : (
            <>
              {request.clientOrigin && (
                <span>
                  Published at{' '}
                  <span className="text-[#AEB8C8]" style={{ fontFamily: MONO }}>
                    {request.clientOrigin}
                  </span>
                </span>
              )}
              <span>
                Returns to{' '}
                <span className="text-[#AEB8C8]" style={{ fontFamily: MONO }}>
                  {request.returnTo}
                </span>
              </span>
              {isOwnComputer(request.returnTo) && (
                <span className="text-[#F3C969]">Only continue if you started this on your computer.</span>
              )}
            </>
          )}
        </Fine>
      </Card>
    </Page>
  );
};

export default ConnectAgent;
